/**
 * Nexion NightWatch - Full Local Simulation Scenario Utility
 *
 * Runs an offline, comprehensive end-to-end simulation across all proximity lifecycle use cases:
 *
 * SPECIFICATION & ALERT MESSAGING REFERENCE:
 * --------------------------------------------------------------------------------------------------
 * | State / Zone   | Survivor device alert message text            | Perpetrator device alert message text  | Speed |
 * | -------------- | --------------------------------------------- | -------------------------------------- | ----- |
 * | Cleared (>2km) | No zone alert (watches stay silent)           | No zone alert (watches stay silent)    | 30s   |
 * | Buffer (1k-2km)| No zone alert (watches stay silent)           | No zone alert (watches stay silent)    | 7s    |
 * | Yellow Zone    | Notice: possible approach nearby. Stay alert. | Restricted zone alert: leave immediately| 7s   |
 * | Red Zone       | Alert: threat is closing in. Stay safe.       | Immediate alert: leave this area now.  | 3s    |
 * | Step-down Red→Y| Notice: possible approach nearby. Stay alert. | Restricted zone alert: leave immediately| 7s   |
 * | Exit into buf  | Alert cleared (monitoring buffer)             | Alert cleared                          | 7s    |
 * | Exit past buf  | Notice: All Clear (survivor only)             | No zone alert (watches stay silent)    | 30s   |
 * --------------------------------------------------------------------------------------------------
 *
 * Covers all client use cases in a single demonstration:
 * 1. Safe outside monitoring (approach from ~3.7 km, 30s speed).
 * 2. Inbound buffer zone entry (~2 km, 7s speed, watches silent).
 * 3. Yellow Zone entry (Yellow banner, 7s speed, Notice / Restricted alerts).
 * 4. Red Zone emergency breach (Red banner, 3s speed, Alert / Immediate alerts).
 * 5. Boundary loitering & 50m exit buffer test (proves no alert flapping within hysteresis).
 * 6. Step down from Red back into Yellow (Yellow banner returns, 7s speed; step down, not all-clear).
 * 7. Exit past Yellow into buffer zone (banner gone, 7s speed, watches idle).
 * 8. Clean exit past 2× Yellow buffer zone (> 2,000m, 30s speed, survivor notified: "Notice: All Clear").
 * 7. Safe monitoring baseline restored.
 *
 * Operates purely locally without making real check-in HTTP requests or live WebSockets.
 */

import { LocationPair, SimulationActivityType } from './simulation.util';
import { AdvocateLogMessage, ProximityAlertMessage } from '../services';
import { haversineDistanceMeters, getDistanceParameterData, getDistanceParameterLabel } from './geo.util';

/**
 * Formats standard advocate event log message matching server specification:
 * e.g. "2310.990 yd detected — Threshold breached (1093.61 yd)"
 */
export function formatAdvocateLogMessage(
  distanceMeters: number,
  thresholdMeters: number,
  distanceParameter = 'yard',
): string {
  const unit = getDistanceParameterLabel(distanceParameter);
  const distance = getDistanceParameterData(distanceMeters, distanceParameter);
  const threshold = getDistanceParameterData(thresholdMeters, distanceParameter);
  return `${distance.toFixed(3)} ${unit} detected — Threshold breached (${threshold} ${unit})`;
}

/** Official alert texts per product specification */
export const PROXIMITY_ALERT_TEXTS = {
  ALERT_TITLE: 'Proximity Alert',
  YELLOW: {
    SURVIVOR: {
      title: 'Proximity Alert',
      body: 'Notice: possible approach nearby. Stay alert.',
    },
    PERPETRATOR: {
      title: 'Proximity Alert',
      body: 'Restricted zone alert: leave immediately.',
    },
  },
  RED: {
    SURVIVOR: {
      title: 'Proximity Alert',
      body: 'Alert: threat is closing in. Stay safe.',
    },
    PERPETRATOR: {
      title: 'Proximity Alert',
      body: 'Immediate alert: leave this area now.',
    },
  },
  ALL_CLEAR: {
    SURVIVOR: {
      title: 'Proximity Alert',
      body: 'Notice: All Clear',
    },
  },
};

export interface LocalSimulationAlertPayload {
  watch: 'survivor' | 'perpetrator' | 'both';
  survivorAlert?: ProximityAlertMessage;
  perpetratorAlert?: ProximityAlertMessage;
  alert?: ProximityAlertMessage;
}

export interface LocalSimulationStep {
  stepIndex: number;
  survivor: LocationPair;
  perpetrator: LocationPair;
  activity: SimulationActivityType;
  survivorCheckInTimeSeconds: number;
  perpetratorCheckInTimeSeconds: number;
  distanceMeters: number;
  zone: 'red' | 'yellow' | 'outside';
  phaseTitle: string;
  phaseDescription: string;
  alert?: LocalSimulationAlertPayload;
  advocateLog?: AdvocateLogMessage;
  clearAlerts?: boolean;
}

export interface LocalSimulationProgress {
  completedCheckIns: number;
  totalCheckIns: number;
  remainingCheckIns: number;
  currentPhaseTitle: string;
}

export interface LocalSimulationOptions {
  intervalMs?: number;
  survivorName?: string;
  perpetratorName?: string;
  survivorLocations?: LocationPair[];
  perpetratorLocations?: LocationPair[];
  yellowThresholdMeters?: number;
  redThresholdMeters?: number;
  bufferThresholdMeters?: number;
  distanceParameter?: string;
  onStep: (step: LocalSimulationStep, progress: LocalSimulationProgress) => void;
  onComplete?: () => void;
  onError?: (err: unknown) => void;
}

export interface ScenarioMilestone {
  pt: LocationPair;
  steps: number;
  yd: number;
  zone: 'buffer' | 'yellow' | 'red' | 'cleared';
  phaseTitle: string;
  phaseDescription: string;
  skipAdvocateLog?: boolean;
}

/**
 * 14 explicit proximity scenario milestones starting outside the buffer zone (cleared area)
 * and progressing through the 13 advocate log approach/breach/clear events:
 * 0. 2624.620 yd - cleared (Initial start point outside buffer zone at ~2.4 km)
 * 1. 1496.520 yd - buffer
 * 2. 1311.060 yd - buffer
 * 3. 1026.620 yd - yellow
 * 4. 441.070 yd  - red
 * 5. 1884.950 yd - buffer
 * 6. 3353.320 yd - cleared
 * 7. 1788.540 yd - buffer
 * 8. 890.410 yd  - yellow
 * 9. 384.950 yd  - red
 * 10. 339.020 yd - red
 * 11. 804.820 yd - yellow
 * 12. 1630.090 yd - buffer
 * 13. 2310.990 yd - cleared
 */
export const LOCAL_SCENARIO_MILESTONES: ScenarioMilestone[] = [
  {
    pt: [-80.7671726, 35.3807512],
    steps: 7,
    yd: 2624.62,
    zone: 'cleared',
    phaseTitle: 'Phase 0: Outside Buffer Zone (Cleared Area)',
    phaseDescription: 'Perpetrator initial position outside buffer zone (2624.620 yd). Status cleared. Check-in speed 30s.',
    skipAdvocateLog: true,
  },
  {
    pt: [-80.7752178, 35.3741916],
    steps: 9,
    yd: 1496.52,
    zone: 'buffer',
    phaseTitle: 'Phase 1: Inbound Approach in Buffer Zone',
    phaseDescription: 'Perpetrator detected inside outer buffer zone (1496.520 yd). Check-in speed 7s.',
  },
  {
    pt: [-80.776064, 35.3727037],
    steps: 4,
    yd: 1311.06,
    zone: 'buffer',
    phaseTitle: 'Phase 1: Inbound Buffer Approach',
    phaseDescription: 'Perpetrator closing in through buffer zone (1311.060 yd). Check-in speed 7s.',
  },
  {
    pt: [-80.7777315, 35.3706873],
    steps: 8,
    yd: 1026.62,
    zone: 'yellow',
    phaseTitle: 'Phase 2: Yellow Zone Warning Entry',
    phaseDescription: 'Yellow Zone breached (1026.620 yd). Warning active on both devices. Check-in speed 7s.',
  },
  {
    pt: [-80.782038, 35.3673035],
    steps: 27,
    yd: 441.07,
    zone: 'red',
    phaseTitle: 'Phase 3: Red Zone Emergency Breach',
    phaseDescription: 'Emergency breach into Red Zone (441.070 yd). Emergency alert active on both devices. Check-in speed 3s.',
  },
  {
    pt: [-80.76943, 35.3577385],
    steps: 2,
    yd: 1884.95,
    zone: 'buffer',
    phaseTitle: 'Phase 4: Exiting Yellow into Buffer Zone',
    phaseDescription: 'Perpetrator departed into buffer zone (1884.950 yd). Watches idle. Check-in speed 7s.',
  },
  {
    pt: [-80.7619846, 35.3459886],
    steps: 14,
    yd: 3353.32,
    zone: 'cleared',
    phaseTitle: 'Phase 5: Cleared Outer Buffer & All Clear',
    phaseDescription: 'Perpetrator cleared outer buffer zone (3353.320 yd). Survivor notified: Notice: All Clear. Check-in speed 30s.',
  },
  {
    pt: [-80.7755465, 35.3534416],
    steps: 3,
    yd: 1788.54,
    zone: 'buffer',
    phaseTitle: 'Phase 6: Re-entry into Buffer Zone',
    phaseDescription: 'Perpetrator re-entering buffer zone (1788.540 yd). Watches idle. Check-in speed 7s.',
  },
  {
    pt: [-80.7835662, 35.3584173],
    steps: 10,
    yd: 890.41,
    zone: 'yellow',
    phaseTitle: 'Phase 7: Second Yellow Zone Breach',
    phaseDescription: 'Second approach enters Yellow Zone (890.410 yd). Warning active on both devices. Check-in speed 7s.',
  },
  {
    pt: [-80.78589, 35.3623244],
    steps: 12,
    yd: 384.95,
    zone: 'red',
    phaseTitle: 'Phase 8: Second Red Zone Emergency Breach',
    phaseDescription: 'Second approach breaches Red Zone (384.950 yd). Emergency alert on both devices. Check-in speed 3s.',
  },
  {
    pt: [-80.7870592, 35.3628702],
    steps: 4,
    yd: 339.02,
    zone: 'red',
    phaseTitle: 'Phase 9: Deep Red Zone Loitering',
    phaseDescription: 'Deepest Red Zone position (339.020 yd). Emergency alert on both devices. Check-in speed 3s.',
  },
  {
    pt: [-80.7921068, 35.3612356],
    steps: 5,
    yd: 804.82,
    zone: 'yellow',
    phaseTitle: 'Phase 10: Step Down from Red to Yellow',
    phaseDescription: 'Stepped down from Red to Yellow (804.820 yd). Step down, not all-clear. Check-in speed 7s.',
  },
  {
    pt: [-80.8013359, 35.3609043],
    steps: 2,
    yd: 1630.09,
    zone: 'buffer',
    phaseTitle: 'Phase 11: Exit Yellow into Buffer Zone',
    phaseDescription: 'Exited Yellow into buffer zone (1630.090 yd). Watches idle. Check-in speed 7s.',
  },
  {
    pt: [-80.8091945, 35.3654878],
    steps: 0,
    yd: 2310.99,
    zone: 'cleared',
    phaseTitle: 'Phase 12: Outside Buffer Zone & Final All Clear',
    phaseDescription: 'Cleared outer buffer zone (2310.990 yd). Survivor notified: Notice: All Clear. Check-in speed 30s.',
  },
];

function interpolateMilestones(milestones: ScenarioMilestone[]): {
  points: LocationPair[];
  milestoneStepIndices: Map<number, number>;
} {
  const points: LocationPair[] = [];
  const milestoneStepIndices = new Map<number, number>();

  for (let i = 0; i < milestones.length - 1; i++) {
    const m1 = milestones[i];
    const m2 = milestones[i + 1];
    milestoneStepIndices.set(points.length, i);
    for (let s = 0; s < m1.steps; s++) {
      const t = s / m1.steps;
      points.push([
        Number((m1.pt[0] + (m2.pt[0] - m1.pt[0]) * t).toFixed(7)),
        Number((m1.pt[1] + (m2.pt[1] - m1.pt[1]) * t).toFixed(7)),
      ]);
    }
  }
  milestoneStepIndices.set(points.length, milestones.length - 1);
  points.push(milestones[milestones.length - 1].pt);

  return { points, milestoneStepIndices };
}

const { points: PERPETRATOR_TRAJECTORY_POINTS, milestoneStepIndices } =
  interpolateMilestones(LOCAL_SCENARIO_MILESTONES);

/** Standard baseline coordinates centered in Charlotte NC */
export const BASELINE_SURVIVOR: LocationPair = [-80.78589, 35.36549];

/**
 * 108-step smooth curated trajectory covering the entire proximity lifecycle.
 * Coordinates are formatted as [longitude, latitude].
 */
export const LOCAL_FULL_SCENARIO_COORDINATES: {
  survivor: LocationPair[];
  perpetrator: LocationPair[];
} = {
  survivor: Array(PERPETRATOR_TRAJECTORY_POINTS.length).fill(BASELINE_SURVIVOR),
  perpetrator: PERPETRATOR_TRAJECTORY_POINTS,
};

/**
 * Builds the smooth curated local simulation conforming strictly to the specification:
 * - Cleared (> 2,000m): 30s check-in speed, Notice: All Clear to survivor once cleared.
 * - Buffer zone (1,000m - 2,000m): 7s check-in speed, alert cleared, watches idle.
 * - Yellow: 7s check-in speed, Alert ALWAYS shown on both watches while in Yellow.
 * - Red: 3s check-in speed, Alert ALWAYS shown on both watches while in Red.
 * - Emits the 13 exact advocate event logs specified at milestone arrival.
 */
export function buildLocalSimulationSteps(
  survivorName = 'Survivor',
  perpetratorName = 'Perpetrator',
  yellowThresholdMeters = 1000,
  redThresholdMeters = 500,
  bufferThresholdMeters = 2000,
  distanceParameter = 'yard',
): LocalSimulationStep[] {
  const steps: LocalSimulationStep[] = [];
  const coords = LOCAL_FULL_SCENARIO_COORDINATES;
  const count = coords.perpetrator.length;
  let hasBreached = false;
  let allClearEmitted = false;

  for (let i = 0; i < count; i++) {
    const s = coords.survivor[i];
    const p = coords.perpetrator[i];
    const rawDistanceMeters = haversineDistanceMeters(
      { lat: s[1], lng: s[0] },
      { lat: p[1], lng: p[0] },
    );
    const distanceMeters = Math.round(rawDistanceMeters);

    const isRed = distanceMeters <= redThresholdMeters;
    const isYellow = !isRed && distanceMeters <= yellowThresholdMeters;
    const isBuffer = !isRed && !isYellow && distanceMeters <= bufferThresholdMeters;

    const currentZone: 'red' | 'yellow' | 'outside' = isRed
      ? 'red'
      : isYellow
        ? 'yellow'
        : 'outside';

    if (isRed || isYellow) {
      hasBreached = true;
      allClearEmitted = false;
    }

    let speedSeconds = 30;
    if (isRed) {
      speedSeconds = 3;
    } else if (isYellow || isBuffer) {
      speedSeconds = 7;
    } else {
      speedSeconds = 30;
    }

    const nowIso = new Date().toISOString();
    let alert: LocalSimulationAlertPayload | undefined;
    let clearAlerts = false;
    let advocateLog: AdvocateLogMessage | undefined;
    let activity: SimulationActivityType = 'WALKING';

    const milestoneIndex = milestoneStepIndices.get(i);
    let phaseTitle = 'Monitoring Outside';
    let phaseDescription = `${perpetratorName} is ${distanceMeters} m from ${survivorName}. Check-in speed ${speedSeconds}s.`;

    if (milestoneIndex !== undefined) {
      const milestone = LOCAL_SCENARIO_MILESTONES[milestoneIndex];
      phaseTitle = milestone.phaseTitle;
      phaseDescription = milestone.phaseDescription;
      if (!milestone.skipAdvocateLog) {
        advocateLog = {
          message: formatAdvocateLogMessage(rawDistanceMeters, yellowThresholdMeters, distanceParameter),
          zone: milestone.zone,
          receivedAt: new Date(),
        };
      }
    }

    if (isRed) {
      activity = distanceMeters <= 340 ? 'STILL' : 'WALKING';
      alert = {
        watch: 'both',
        survivorAlert: {
          title: PROXIMITY_ALERT_TEXTS.ALERT_TITLE,
          body: PROXIMITY_ALERT_TEXTS.RED.SURVIVOR.body,
          sendAt: nowIso,
        },
        perpetratorAlert: {
          title: PROXIMITY_ALERT_TEXTS.ALERT_TITLE,
          body: PROXIMITY_ALERT_TEXTS.RED.PERPETRATOR.body,
          sendAt: nowIso,
        },
      };
      clearAlerts = false;
    } else if (isYellow) {
      activity = 'WALKING';
      alert = {
        watch: 'both',
        survivorAlert: {
          title: PROXIMITY_ALERT_TEXTS.ALERT_TITLE,
          body: PROXIMITY_ALERT_TEXTS.YELLOW.SURVIVOR.body,
          sendAt: nowIso,
        },
        perpetratorAlert: {
          title: PROXIMITY_ALERT_TEXTS.ALERT_TITLE,
          body: PROXIMITY_ALERT_TEXTS.YELLOW.PERPETRATOR.body,
          sendAt: nowIso,
        },
      };
      clearAlerts = false;
    } else if (isBuffer) {
      activity = 'WALKING';
      alert = undefined;
      clearAlerts = true;
    } else {
      // Cleared (> bufferThresholdMeters)
      activity = i === count - 1 ? 'STILL' : 'WALKING';
      if (hasBreached && !allClearEmitted) {
        allClearEmitted = true;
        hasBreached = false;
        alert = {
          watch: 'survivor',
          survivorAlert: {
            title: PROXIMITY_ALERT_TEXTS.ALERT_TITLE,
            body: PROXIMITY_ALERT_TEXTS.ALL_CLEAR.SURVIVOR.body,
            sendAt: nowIso,
          },
        };
        clearAlerts = false;
      } else {
        alert = undefined;
        clearAlerts = false;
      }
    }

    steps.push({
      stepIndex: i + 1,
      survivor: s,
      perpetrator: p,
      activity,
      survivorCheckInTimeSeconds: speedSeconds,
      perpetratorCheckInTimeSeconds: speedSeconds,
      distanceMeters,
      zone: currentZone,
      phaseTitle,
      phaseDescription,
      alert,
      advocateLog,
      clearAlerts,
    });
  }

  return steps;
}

/**
 * Builds dynamic steps from arbitrary survivor/perpetrator tracks, applying the exact
 * alert texts and check-in cadence according to separation distance:
 * - Cleared (> 2× Yellow): 30s check-in speed.
 * - Buffer zone (Yellow to 2× Yellow): 7s check-in speed, alert cleared.
 * - Yellow zone: 7s check-in speed, Alert ALWAYS shown on watches while in Yellow.
 * - Red zone: 3s check-in speed, Alert ALWAYS shown on watches while in Red.
 * - Outside buffer zone (> 2× Yellow): Survivor notified "Notice: All Clear" (30s speed).
 */
export function buildDynamicLocalSteps(
  survivorCoords: LocationPair[],
  perpetratorCoords: LocationPair[],
  survivorName = 'Survivor',
  perpetratorName = 'Perpetrator',
  yellowThresholdMeters = 1000,
  redThresholdMeters = 500,
  bufferThresholdMeters = 2000,
  distanceParameter = 'yard',
): LocalSimulationStep[] {
  // If coordinates match the curated full scenario, return the full milestone sequence
  if (
    survivorCoords === LOCAL_FULL_SCENARIO_COORDINATES.survivor ||
    perpetratorCoords === LOCAL_FULL_SCENARIO_COORDINATES.perpetrator ||
    (survivorCoords.length === LOCAL_FULL_SCENARIO_COORDINATES.survivor.length &&
      survivorCoords[0][0] === LOCAL_FULL_SCENARIO_COORDINATES.survivor[0][0] &&
      survivorCoords[0][1] === LOCAL_FULL_SCENARIO_COORDINATES.survivor[0][1])
  ) {
    return buildLocalSimulationSteps(
      survivorName,
      perpetratorName,
      yellowThresholdMeters,
      redThresholdMeters,
      bufferThresholdMeters,
      distanceParameter,
    );
  }

  const count = Math.min(survivorCoords.length, perpetratorCoords.length);
  const steps: LocalSimulationStep[] = [];
  let currentZone: 'red' | 'yellow' | 'outside' = 'outside';
  let hasBreachedRedOrYellow = false;
  let loggedInboundBuffer = false;
  let loggedYellowEntry = false;
  let loggedRedEntry = false;
  let loggedBufferExit = false;
  let loggedAllClear = false;

  for (let i = 0; i < count; i++) {
    const s = survivorCoords[i];
    const p = perpetratorCoords[i];
    const rawDistanceMeters = haversineDistanceMeters(
      { lat: s[1], lng: s[0] },
      { lat: p[1], lng: p[0] },
    );
    const distanceMeters = Math.round(rawDistanceMeters);

    let nextZone: 'red' | 'yellow' | 'outside';
    if (currentZone === 'red') {
      if (distanceMeters > redThresholdMeters + 50) {
        nextZone = distanceMeters <= yellowThresholdMeters ? 'yellow' : 'outside';
      } else {
        nextZone = 'red';
      }
    } else if (currentZone === 'yellow') {
      if (distanceMeters <= redThresholdMeters) {
        nextZone = 'red';
      } else if (distanceMeters > yellowThresholdMeters) {
        nextZone = 'outside';
      } else {
        nextZone = 'yellow';
      }
    } else {
      if (distanceMeters <= redThresholdMeters) {
        nextZone = 'red';
      } else if (distanceMeters <= yellowThresholdMeters) {
        nextZone = 'yellow';
      } else {
        nextZone = 'outside';
      }
    }

    const wasRed = currentZone === 'red';
    currentZone = nextZone;
    if (currentZone === 'yellow' || currentZone === 'red') {
      hasBreachedRedOrYellow = true;
      loggedAllClear = false;
      loggedBufferExit = false;
    }

    let speedSeconds = 30;
    if (currentZone === 'red') {
      speedSeconds = 3;
    } else if (currentZone === 'yellow') {
      speedSeconds = 7;
    } else if (distanceMeters <= bufferThresholdMeters) {
      speedSeconds = 7;
    } else {
      speedSeconds = 30;
    }

    let alert: LocalSimulationAlertPayload | undefined;
    let clearAlerts = false;
    let advocateLog: AdvocateLogMessage | undefined;
    let phaseTitle = 'Monitoring Outside';
    let phaseDescription = `${perpetratorName} is ${distanceMeters} m from ${survivorName}. Check-in speed ${speedSeconds}s.`;
    let activity: SimulationActivityType = 'WALKING';
    const nowIso = new Date().toISOString();

    if (currentZone === 'red') {
      activity = distanceMeters <= 300 ? 'STILL' : 'WALKING';
      phaseTitle = 'Red Zone Emergency Breach';
      phaseDescription = `Inside Red Zone (${distanceMeters} m). Check-in speed ${speedSeconds}s. Emergency alert active on both devices.`;

      alert = {
        watch: 'both',
        survivorAlert: {
          title: PROXIMITY_ALERT_TEXTS.ALERT_TITLE,
          body: PROXIMITY_ALERT_TEXTS.RED.SURVIVOR.body,
          sendAt: nowIso,
        },
        perpetratorAlert: {
          title: PROXIMITY_ALERT_TEXTS.ALERT_TITLE,
          body: PROXIMITY_ALERT_TEXTS.RED.PERPETRATOR.body,
          sendAt: nowIso,
        },
      };
      clearAlerts = false;

      if (!loggedRedEntry) {
        loggedRedEntry = true;
        advocateLog = {
          message: formatAdvocateLogMessage(rawDistanceMeters, yellowThresholdMeters, distanceParameter),
          zone: 'red',
          receivedAt: new Date(),
        };
      }
    } else if (currentZone === 'yellow') {
      phaseTitle = wasRed ? 'Step Down from Red to Yellow' : 'Yellow Zone Warning Entry';
      phaseDescription = `Inside Yellow Zone (${distanceMeters} m). Check-in speed ${speedSeconds}s. Warning active on both devices.`;

      alert = {
        watch: 'both',
        survivorAlert: {
          title: PROXIMITY_ALERT_TEXTS.ALERT_TITLE,
          body: PROXIMITY_ALERT_TEXTS.YELLOW.SURVIVOR.body,
          sendAt: nowIso,
        },
        perpetratorAlert: {
          title: PROXIMITY_ALERT_TEXTS.ALERT_TITLE,
          body: PROXIMITY_ALERT_TEXTS.YELLOW.PERPETRATOR.body,
          sendAt: nowIso,
        },
      };
      clearAlerts = false;

      if (!loggedYellowEntry) {
        loggedYellowEntry = true;
        advocateLog = {
          message: formatAdvocateLogMessage(rawDistanceMeters, yellowThresholdMeters, distanceParameter),
          zone: 'yellow',
          receivedAt: new Date(),
        };
      }
    } else {
      activity = i === count - 1 ? 'STILL' : 'WALKING';
      if (hasBreachedRedOrYellow) {
        if (distanceMeters <= bufferThresholdMeters) {
          clearAlerts = true;
          alert = undefined;
          phaseTitle = 'Exiting Yellow into Buffer Zone';
          phaseDescription = `${perpetratorName} exited Yellow Zone into buffer zone (${distanceMeters} m). Monitoring until clear of outer buffer. Check-in speed ${speedSeconds}s.`;

          if (!loggedBufferExit) {
            loggedBufferExit = true;
            advocateLog = {
              message: formatAdvocateLogMessage(rawDistanceMeters, yellowThresholdMeters, distanceParameter),
              zone: 'buffer',
              receivedAt: new Date(),
            };
          }
        } else {
          phaseTitle = 'Outside Buffer Zone & All Clear';
          phaseDescription = `${perpetratorName} cleared outer buffer zone (${distanceMeters} m). Survivor notified: Notice: All Clear. Check-in speed ${speedSeconds}s.`;

          if (!loggedAllClear) {
            loggedAllClear = true;
            hasBreachedRedOrYellow = false;
            alert = {
              watch: 'survivor',
              survivorAlert: {
                title: PROXIMITY_ALERT_TEXTS.ALERT_TITLE,
                body: PROXIMITY_ALERT_TEXTS.ALL_CLEAR.SURVIVOR.body,
                sendAt: nowIso,
              },
            };
            clearAlerts = false;
            advocateLog = {
              message: formatAdvocateLogMessage(rawDistanceMeters, yellowThresholdMeters, distanceParameter),
              zone: 'cleared',
              receivedAt: new Date(),
            };
          } else {
            alert = undefined;
            clearAlerts = false;
          }
        }
      } else {
        clearAlerts = true;
        alert = undefined;
        if (distanceMeters <= bufferThresholdMeters) {
          phaseTitle = 'Inbound Approach in Buffer Zone';
          phaseDescription = `${perpetratorName} approaching inside buffer zone (${distanceMeters} m). Check-in speed ${speedSeconds}s. Watches stay silent.`;

          if (!loggedInboundBuffer) {
            loggedInboundBuffer = true;
            advocateLog = {
              message: formatAdvocateLogMessage(rawDistanceMeters, yellowThresholdMeters, distanceParameter),
              zone: 'buffer',
              receivedAt: new Date(),
            };
          }
        }
      }
    }

    steps.push({
      stepIndex: i + 1,
      survivor: s,
      perpetrator: p,
      activity,
      survivorCheckInTimeSeconds: speedSeconds,
      perpetratorCheckInTimeSeconds: speedSeconds,
      distanceMeters,
      zone: currentZone,
      phaseTitle,
      phaseDescription,
      alert,
      advocateLog,
      clearAlerts,
    });
  }

  return steps;
}

let activeIntervalHandle: ReturnType<typeof setInterval> | undefined;
let isLocalSimulationActive = false;

/**
 * Starts the local offline scenario replay.
 * If custom survivor/perpetrator locations are provided, dynamically builds steps from them;
 * otherwise uses the curated trajectory sequence.
 */
export function startLocalSimulation(options: LocalSimulationOptions): void {
  stopLocalSimulation();
  isLocalSimulationActive = true;

  const yellowThreshold = options.yellowThresholdMeters ?? 1000;
  const redThreshold = options.redThresholdMeters ?? 500;
  const bufferThreshold = options.bufferThresholdMeters ?? yellowThreshold * 2;
  const distanceParam = options.distanceParameter ?? 'yard';

  const hasCustomCoords =
    options.survivorLocations?.length &&
    options.perpetratorLocations?.length &&
    options.survivorLocations.length > 1;

  const steps = hasCustomCoords
    ? buildDynamicLocalSteps(
      options.survivorLocations!,
      options.perpetratorLocations!,
      options.survivorName,
      options.perpetratorName,
      yellowThreshold,
      redThreshold,
      bufferThreshold,
      distanceParam,
    )
    : buildLocalSimulationSteps(
      options.survivorName,
      options.perpetratorName,
      yellowThreshold,
      redThreshold,
      bufferThreshold,
      distanceParam,
    );

  const totalSteps = steps.length;
  let currentIndex = 0;
  const interval = options.intervalMs ?? 1000;

  const emitNext = () => {
    if (!isLocalSimulationActive) {
      return;
    }
    if (currentIndex >= totalSteps) {
      stopLocalSimulation();
      options.onComplete?.();
      return;
    }

    const step = steps[currentIndex];
    const progress: LocalSimulationProgress = {
      completedCheckIns: currentIndex + 1,
      totalCheckIns: totalSteps,
      remainingCheckIns: totalSteps - (currentIndex + 1),
      currentPhaseTitle: step.phaseTitle,
    };

    try {
      options.onStep(step, progress);
    } catch (err) {
      options.onError?.(err);
    }

    currentIndex++;
  };

  emitNext();
  activeIntervalHandle = setInterval(emitNext, interval);
}

/**
 * Stops any actively running local simulation.
 */
export function stopLocalSimulation(): void {
  if (activeIntervalHandle) {
    clearInterval(activeIntervalHandle);
    activeIntervalHandle = undefined;
  }
  isLocalSimulationActive = false;
}

export function isLocalSimulationRunning(): boolean {
  return isLocalSimulationActive;
}
